import {observer} from "mobx-react";
import IconCirclePlaceholder from "../../../../../components/icons/icon-circle-placeholder/icon-circle-placeholder";
import IconCircleInTransit from "../../../../../components/icons/icon-circle-in-transit/icon-circle-in-transit";
import IconCircleNotReturned from "../../../../../components/icons/icon-circle-not-returned/icon-circle-not-returned";
import IconCircleReturned from "../../../../../components/icons/icon-circle-returned/icon-circle-returned";
import IconCircleRestockable from "../../../../../components/icons/icon-circle-restockable/icon-circle-restockable";
import IconCircleNotRestockable from "../../../../../components/icons/icon-circle-not-restockable/icon-circle-not-restockable";
import IconCircleRestocked from "../../../../../components/icons/icon-circle-restocked/icon-circle-restocked";
import Icon from '../../../../../components/icons/icon/icon';
import { TOOLTIP_VARIANTS, Tooltip } from '../../../../../components/tooltip/tooltip';
import { TOOLTIP_PLACEMENTS } from '../../../../../components/tooltip/tooltip-box';
import { ICON_SIZE } from '../../../../../components/icons/icon-props-interface';
import ProductCaseStore from "../../stores/product-case-store";
import CustomerCaseStore from "../../stores/customer-case-store";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import Currency from "../../../../../components/currency/currency";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import './rma-product-indications-icons.scss';
import IconCredited from "../../../../../components/icons/icon-credited/icon-credited";
import IconRefunded from "../../../../../components/icons/icon-refunded/icon-refunded";
import IconDiscount from "../../../../../components/icons/icon-discount/icon-discount";
import TextTruncate from "../../../../../components/text-truncate/text-truncate";

interface IProps {
    productCaseStore: ProductCaseStore;
    customerCaseStore: CustomerCaseStore;
}

const IndicationPlaceholder = () => (<IconCirclePlaceholder color="transparent" />)

const RmaProductIndicationsIcons: React.FC<IProps> = observer(({productCaseStore, customerCaseStore}) => {
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-returns-rma-card';

    const ShippedIcon = productCaseStore.hasInTransitShipment()
        ? IconCircleInTransit
        : IndicationPlaceholder;
    const ReturnedIcon = productCaseStore.isReturned === null || customerCaseStore.isCancelResolution()
        ? IndicationPlaceholder
        : productCaseStore.isReturned === true ? IconCircleReturned : IconCircleNotReturned;
    const RestockableIcon = productCaseStore.isRestockable === null || customerCaseStore.isCancelResolution()
        ? IndicationPlaceholder
        : (productCaseStore.isRestockable ? IconCircleRestockable : IconCircleNotRestockable);
    // TODO: remove isCancelResolution check when https://returngo.atlassian.net/browse/RD-6602 is done
    // see https://returngo.atlassian.net/browse/RD-6603
    const RestockedIcon = productCaseStore.isRestocked && !customerCaseStore.isCancelResolution() ? IconCircleRestocked : IndicationPlaceholder;
    const RefundedIcon = productCaseStore.isRefunded ? IconRefunded : IndicationPlaceholder;
    const CreditedIcon = (productCaseStore.isCredited || productCaseStore.isRefundedToGiftCard || !!productCaseStore.automaticallyCreditedAmount || !!productCaseStore.automaticallyGiftedAmount) ? IconCredited : IndicationPlaceholder;

    const refundTooltipHeading = productCaseStore.isAutomaticallyRefunded 
        ? getTranslation('AUTOMATICALLY_REFUNDED', pageScope)
        : (productCaseStore.externalRefundAmount)
            ? 'Refunded (Externally)'
            : 'Refunded';

            const discountTooltipContent = () => {
                return (
                    <div className={"discount-tooltip-content"}>
                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE} variant={FONT_VARIANT.BOLD}>{getTranslation('DISCOUNT_TOOLTIP_TITLE', pageScope)}:</FontBody2>
                        <ul>
                            {productCaseStore.isPartOfCompareAtPriceDiscount && (
                                <li>
                                    <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTranslation('DISCOUNT_TOOLTIP_DISCOUNT_TYPE_CAP',pageScope)}</FontBody2>
                                </li>
                            )}
                            {productCaseStore.isPartOfBxGyDiscount && 
                                <>
                                    <li>
                                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTranslation('DISCOUNT_TOOLTIP_DISCOUNT_TYPE_BXGY',pageScope)}:</FontBody2>
                                    </li>
                                    <ul>
                                        <li className="discount-tooltip-content-code-row">
                                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTranslation('DISCOUNT_TOOLTIP_DISCOUNT_CODE',pageScope)}:</FontBody2>
                                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}><TextTruncate maxWidth={{ value: 150, type: 'px' }}>{productCaseStore.bxgyDiscountCode}</TextTruncate></FontBody2>
                                        </li>
                                        {productCaseStore.isBx && (
                                            <li>
                                                <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTranslation('DISCOUNT_TOOLTIP_B_X_ITEM',pageScope)}</FontBody2>
                                            </li>
                                        )}
                                        {productCaseStore.isGy && (
                                            <li>
                                                <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTranslation('DISCOUNT_TOOLTIP_G_Y_ITEM',pageScope)}</FontBody2>
                                            </li>
                                        )}
                                    </ul>
                                </>
                                
                            }
                        </ul>
                    </div>
                );
            }

    return (
        <div className={"rma-indications-icons"}>
            {(productCaseStore.isPartOfBxGyDiscount || productCaseStore.isPartOfCompareAtPriceDiscount) && (
                <div className={"discount-icon-container"}>
                    <Tooltip
                        content={discountTooltipContent()}
                        placement={TOOLTIP_PLACEMENTS.TOP}
                        variant={TOOLTIP_VARIANTS.INFO}
                    >
                        <Icon Component={IconDiscount} size={ICON_SIZE.MEDIUM} />
                    </Tooltip>
                </div>
            )}
            <Tooltip
                content={productCaseStore.hasInTransitShipment() && (
                    <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                        {getTranslation('IN_TRANSIT', 'dashboard-returns-page')}
                    </FontBody2>
                )}
                placement={TOOLTIP_PLACEMENTS.TOP}
                variant={TOOLTIP_VARIANTS.INFO}
            >
                <Icon Component={ShippedIcon} size={ICON_SIZE.MEDIUM} />
            </Tooltip>
            <Tooltip
                content={productCaseStore.isReturned !== null && !customerCaseStore.isCancelResolution() && (
                    <>
                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>Returned</FontBody2>
                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE} variant={FONT_VARIANT.BOLD}>
                            {productCaseStore.isReturned ? 'Yes' : 'No'}
                        </FontBody2>
                    </>
                )}
                placement={TOOLTIP_PLACEMENTS.TOP}
                variant={TOOLTIP_VARIANTS.INFO}
            >
                <Icon Component={ReturnedIcon} size={ICON_SIZE.MEDIUM} />
            </Tooltip>
            <Tooltip
                content={productCaseStore.isRestockable !== null && !customerCaseStore.isCancelResolution() && (
                    <>
                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>Restock</FontBody2>
                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE} variant={FONT_VARIANT.BOLD}>
                            {productCaseStore.isRestockable ? 'Yes' : 'No'}
                        </FontBody2>
                    </>
                )}
                placement={TOOLTIP_PLACEMENTS.TOP}
                variant={TOOLTIP_VARIANTS.INFO}
            >
                <Icon Component={RestockableIcon} size={ICON_SIZE.MEDIUM} />
            </Tooltip>
            <Tooltip
                /*
                    TODO: remove isCancelResolution check when https://returngo.atlassian.net/browse/RD-6602 is done
                    see https://returngo.atlassian.net/browse/RD-6603
                */
                content={productCaseStore.isRestocked && !customerCaseStore.isCancelResolution() && (
                    <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                        {productCaseStore.restockedPlatforms ? `Restocked on ${productCaseStore.restockedPlatforms}` : "Restocked"}
                    </FontBody2>
                )}
                placement={TOOLTIP_PLACEMENTS.TOP}
                variant={TOOLTIP_VARIANTS.INFO}
            >
                <Icon Component={RestockedIcon} size={ICON_SIZE.MEDIUM} />
            </Tooltip>
            <Tooltip
                content={productCaseStore.isRefunded && (
                    <>
                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{refundTooltipHeading}:</FontBody2>
                        {!!productCaseStore.refundedAmount && (
                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                {"Payment method "}
                                <Currency currencyCode={customerCaseStore.paymentCurrencyCode} amount={productCaseStore.refundedAmount} />
                            </FontBody2>)}
                        {!!productCaseStore.bankRefundedAmount && (
                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                {"Bank account "}
                                <Currency currencyCode={customerCaseStore.paymentCurrencyCode} amount={productCaseStore.bankRefundedAmount} />
                            </FontBody2>)}
                        {!!productCaseStore.externalRefundAmount && (
                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                {"Payment method "}
                                <Currency currencyCode={customerCaseStore.paymentCurrencyCode} amount={productCaseStore.externalRefundAmount} />
                            </FontBody2>)}
                    </>
                )}
                placement={TOOLTIP_PLACEMENTS.TOP}
                variant={TOOLTIP_VARIANTS.INFO}
            >
                <Icon Component={RefundedIcon} size={ICON_SIZE.MEDIUM} />
            </Tooltip>
            <Tooltip
                content={(productCaseStore.isCredited || productCaseStore.isRefundedToGiftCard || !!productCaseStore.automaticallyCreditedAmount || !!productCaseStore.automaticallyGiftedAmount) && (<>
                    {(!!productCaseStore.automaticallyCreditedAmount || !!productCaseStore.automaticallyGiftedAmount) && (
                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{"Automatically credited: "}</FontBody2>
                    )}
                    {!!productCaseStore.automaticallyCreditedAmount && (
                        <>
                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                {getTranslation('STORE_CREDITS', pageScope)}
                                {" "}
                                <Currency currencyCode={customerCaseStore.paymentCurrencyCode} amount={productCaseStore.automaticallyCreditedAmount} />
                            </FontBody2>
                        </>
                    )}
                    {!!productCaseStore.automaticallyGiftedAmount && (
                        <>
                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                {getTranslation('GIFT_CARD', pageScope)}
                                {" "}
                                <Currency currencyCode={customerCaseStore.paymentCurrencyCode} amount={productCaseStore.automaticallyGiftedAmount} />
                            </FontBody2>
                        </>
                    )}

                    {(productCaseStore.isCredited || productCaseStore.isRefundedToGiftCard) && (
                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{"Credited: "}</FontBody2>
                    )}
                    {!!productCaseStore.isCredited && (
                        <>
                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                {getTranslation('STORE_CREDITS', pageScope)}
                                {" "}
                                <Currency currencyCode={customerCaseStore.paymentCurrencyCode} amount={productCaseStore.creditedAmount} />
                            </FontBody2>
                        </>
                    )}
                    {!!productCaseStore.isRefundedToGiftCard && (
                        <>
                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                {getTranslation('GIFT_CARD', pageScope)}
                                {" "}
                                <Currency currencyCode={customerCaseStore.paymentCurrencyCode} amount={productCaseStore.giftedAmount} />
                            </FontBody2>
                        </>
                    )}
                </>)}
                placement={TOOLTIP_PLACEMENTS.TOP}
                variant={TOOLTIP_VARIANTS.INFO}
            >
                <Icon Component={CreditedIcon} size={ICON_SIZE.MEDIUM} />
            </Tooltip>
        </div>
    );
});

export default RmaProductIndicationsIcons;